import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getProjects, getProjectsForSeeker, getProjectDisplayName, type Project } from '@data/projects';
import { useAuth } from '@services/auth';
import { useAdminViewAs, getAdminEffectiveSeekerIdNum, getAdminEffectiveCompanyOrGardenerIdNum } from '../lib/adminViewAs';
import { useI18n } from '../i18n';
import type { GanttTask } from '../types/gantt';
import { getGardeners, type UserResponse } from '@services/users';
import { getCompanyGardeners } from '@services/gardeners';
import {
	getStoredCompanyScopeMode,
	COMPANY_WORKSPACE_CHANGED_EVENT,
	getMyCompany
} from '@services/companies';
import * as tasksAPI from '@services/tasks';
import type { TaskResponse } from '@services/tasks';
import TaskDetailModal from '../components/TaskDetailModal';
import TaskCreateModal from '../components/TaskCreateModal';
import TaskLegalBadge from '../components/TaskLegalBadge';
import CompanyWorkspaceStrip from '../shared/CompanyWorkspaceStrip';
import { getTaskLegalMeta } from '../utils/taskLegalStatus';

// Convert database task to GanttTask format
function dbTaskToGanttTask(dbTask: TaskResponse, project: Project): GanttTask {
	return {
		id: `db-${dbTask.id}`, // Mark as database task
		name: dbTask.title,
		description: dbTask.description || undefined,
		startDate: dbTask.start_date ? new Date(dbTask.start_date).toISOString() : new Date().toISOString(),
		endDate: dbTask.end_date ? new Date(dbTask.end_date).toISOString() : new Date().toISOString(),
		duration: dbTask.duration || 1,
		progress: dbTask.progress || 0,
		status: dbTask.status || 'not_started',
		dependencies: dbTask.dependencies || [],
		isCritical: dbTask.is_critical || false,
		gardenerIds: dbTask.gardener_ids.map(id => String(id)),
		machineIds: dbTask.machine_ids || [],
		projectId: project.id,
		notes: dbTask.notes || undefined,
		// Store gardener names from API response
		gardenerNames: dbTask.gardener_names || [],
		projectCompanyName: dbTask.project_company_name?.trim() || undefined,
		legalSeasonalBanCandidate: Boolean(dbTask.legal_seasonal_ban_candidate),
		legalSeasonalBanReason: dbTask.legal_seasonal_ban_reason || undefined
	} as GanttTask & { gardenerNames?: string[] };
}

export default function Tasks() {
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [projects, setProjects] = useState<Project[]>([]);
	const [databaseTasks, setDatabaseTasks] = useState<TaskResponse[]>([]);
	const [gardeners, setGardeners] = useState<UserResponse[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [selectedTask, setSelectedTask] = useState<{ task: GanttTask; project: Project } | null>(null);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [projectFilterId, setProjectFilterId] = useState<string>('');
	const [scopeFilter, setScopeFilter] = useState<'self' | 'my_projects' | 'company'>(() => {
		if (user?.role === 'company' || user?.role === 'admin') return 'company';
		if (user?.role === 'gardener') return 'self';
		return 'company';
	});
	/** Bumps when workspace strip / top bar sets scope to single-company so we refetch even if activeCompanyId unchanged */
	const [workspaceListRev, setWorkspaceListRev] = useState(0);
	const [statusFilter, setStatusFilter] = useState<'open' | 'completed' | 'all'>('open');
	const [timeFilter, setTimeFilter] = useState<
		'upcoming' | 'overdue' | 'this_week' | 'this_month' | 'next_30' | 'next_90' | 'past' | 'all' | 'range'
	>('upcoming');
	const [rangeFrom, setRangeFrom] = useState<string>('');
	const [rangeTo, setRangeTo] = useState<string>('');
	const [companyCountryCode, setCompanyCountryCode] = useState<string | null>(null);

	useEffect(() => {
		const onWorkspace = () => setWorkspaceListRev((n) => n + 1);
		window.addEventListener(COMPANY_WORKSPACE_CHANGED_EVENT, onWorkspace);
		return () => window.removeEventListener(COMPANY_WORKSPACE_CHANGED_EVENT, onWorkspace);
	}, []);

	const loadData = async () => {
		setIsLoading(true);
		try {
			const showAllCompanies = getStoredCompanyScopeMode() === 'all_companies';
			const adminSeekerLens = user ? getAdminEffectiveSeekerIdNum(user, viewAsTarget) : null;
			const adminCoGardenerLens = user ? getAdminEffectiveCompanyOrGardenerIdNum(user, viewAsTarget) : null;

			// Load projects
			let loadedProjects: Project[];
			if (user?.role === 'admin' && viewAsTarget && adminSeekerLens != null) {
				loadedProjects = (await getProjectsForSeeker(adminSeekerLens)) || [];
			} else if (user?.role === 'admin' && viewAsTarget && adminCoGardenerLens != null) {
				loadedProjects = await getProjects({
					requester_user_id: adminCoGardenerLens,
					company_scope: showAllCompanies ? 'all_companies' : 'company'
				});
			} else if ((user?.role === 'company' || user?.role === 'gardener') && user?.id) {
				const requesterId = parseInt(String(user.id), 10);
				if (!isNaN(requesterId)) {
					loadedProjects = await getProjects({
						requester_user_id: requesterId,
						company_scope: showAllCompanies ? 'all_companies' : 'company'
					});
				} else {
					loadedProjects = [];
				}
			} else if (user?.role === 'admin') {
				loadedProjects = await getProjects();
			} else if (user?.role === 'gardener' && user?.id) {
				loadedProjects = await getProjects(parseInt(String(user.id)));
			} else if (user?.role === 'seeker' && user?.id) {
				// Seekers: only projects where they are the plan owner (their gardens)
				const seekerId = typeof user.id === 'string' ? parseInt(user.id, 10) : user.id;
				loadedProjects = Number.isFinite(seekerId) && !isNaN(seekerId as number)
					? await getProjectsForSeeker(seekerId as number)
					: [];
			} else {
				loadedProjects = await getProjects();
			}
			setProjects(loadedProjects);
			
			// Load tasks from database
			try {
				let loadedTasks: TaskResponse[] = [];
				if (user?.role === 'admin' && viewAsTarget && adminSeekerLens != null) {
					const seekerProjects = loadedProjects;
					if (seekerProjects.length > 0) {
						const projectTasks = await Promise.all(
							seekerProjects.map((project) => {
								const projectId =
									typeof project.id === 'string' ? parseInt(project.id, 10) : project.id;
								return Number.isFinite(projectId)
									? tasksAPI.getTasksForProject(projectId as number)
									: Promise.resolve<TaskResponse[]>([]);
							})
						);
						loadedTasks = projectTasks.flat();
						const uniqueTasks = new Map<number, TaskResponse>();
						loadedTasks.forEach((task) => {
							if (!uniqueTasks.has(task.id)) {
								uniqueTasks.set(task.id, task);
							}
						});
						loadedTasks = Array.from(uniqueTasks.values());
					} else {
						loadedTasks = [];
					}
				} else if (user?.role === 'admin' && viewAsTarget && adminCoGardenerLens != null) {
					loadedTasks = await tasksAPI.getTasks({
						requester_user_id: adminCoGardenerLens,
						company_scope: showAllCompanies ? 'all_companies' : 'company'
					});
				} else if ((user?.role === 'company' || user?.role === 'gardener') && user?.id) {
					const requesterId = parseInt(String(user.id), 10);
					loadedTasks = !isNaN(requesterId)
						? await tasksAPI.getTasks({
							requester_user_id: requesterId,
							company_scope: showAllCompanies ? 'all_companies' : 'company'
						})
						: [];
				} else if (user?.role === 'admin') {
					// Companies and admins see all tasks
					loadedTasks = await tasksAPI.getTasks();
				} else if (user?.role === 'gardener' && user?.id) {
					// For gardeners, load all tasks from their projects
					// First get their projects, then load tasks for those projects
					const gardenerProjects = await getProjects(parseInt(String(user.id)));
					if (gardenerProjects.length > 0) {
						// Load tasks for all projects the gardener has access to
						const projectTasks = await Promise.all(
							gardenerProjects.map(project => tasksAPI.getTasksForProject(typeof project.id === 'string' ? parseInt(project.id) : project.id))
						);
						loadedTasks = projectTasks.flat();
						// Remove duplicates (in case a task appears in multiple calls)
						// Use a Map to ensure we only keep one instance of each task ID
						const uniqueTasks = new Map<number, TaskResponse>();
						loadedTasks.forEach(task => {
							// Only keep the first occurrence of each task ID
							if (!uniqueTasks.has(task.id)) {
								uniqueTasks.set(task.id, task);
							}
						});
						loadedTasks = Array.from(uniqueTasks.values());
					} else {
						// Fallback: show assigned tasks if no projects
						loadedTasks = await tasksAPI.getTasks({ user_id: parseInt(String(user.id)) });
					}
				} else if (user?.role === 'seeker' && user?.id) {
					// Seekers: only tasks from projects that belong to their plans (their gardens)
					const seekerId = typeof user.id === 'string' ? parseInt(user.id, 10) : user.id;
					if (Number.isFinite(seekerId) && !isNaN(seekerId as number)) {
						const seekerProjects = loadedProjects.length > 0
							? loadedProjects
							: await getProjectsForSeeker(seekerId as number);
						if (seekerProjects.length > 0) {
							const projectTasks = await Promise.all(
								seekerProjects
									.map(project => {
										const projectId =
											typeof project.id === 'string'
												? parseInt(project.id, 10)
												: project.id;
										return Number.isFinite(projectId)
											? tasksAPI.getTasksForProject(projectId as number)
											: Promise.resolve<TaskResponse[]>([]);
									})
							);
							loadedTasks = projectTasks.flat();
							// Deduplicate by task ID
							const uniqueTasks = new Map<number, TaskResponse>();
							loadedTasks.forEach(task => {
								if (!uniqueTasks.has(task.id)) {
									uniqueTasks.set(task.id, task);
								}
							});
							loadedTasks = Array.from(uniqueTasks.values());
						} else {
							loadedTasks = [];
						}
					} else {
						loadedTasks = [];
					}
				} else {
					loadedTasks = await tasksAPI.getTasks();
				}
				// Final deduplication pass - ensure no duplicates by ID
				const finalUniqueTasks = new Map<number, TaskResponse>();
				loadedTasks.forEach(task => {
					if (!finalUniqueTasks.has(task.id)) {
						finalUniqueTasks.set(task.id, task);
					}
				});
				const finalTasks = Array.from(finalUniqueTasks.values());
				
				setDatabaseTasks(finalTasks);
			} catch (err) {
				console.error('Failed to load tasks from database:', err);
				setDatabaseTasks([]);
			}
			
			// Load gardeners: company/gardener see only their company; admin sees all
			try {
				let apiGardeners: UserResponse[];
				if (user?.role === 'admin' && viewAsTarget && adminCoGardenerLens != null) {
					const companyGardeners = await getCompanyGardeners(adminCoGardenerLens);
					apiGardeners = companyGardeners.map((g) => ({
						id: g.id,
						name: g.display_name || g.name || g.email || '',
						email: g.email,
						role: g.role,
						company_name: g.company_name,
						created_at: '',
						updated_at: ''
					}));
				} else if (user?.role === 'company' || user?.role === 'gardener') {
					const userIdNum = typeof user.id === 'string' ? parseInt(user.id, 10) : user.id;
					if (!user?.id || isNaN(userIdNum)) {
						apiGardeners = [];
					} else {
						const companyGardeners = await getCompanyGardeners(userIdNum);
						apiGardeners = companyGardeners.map((g) => ({
							id: g.id,
							name: g.display_name || g.name || g.email || '',
							email: g.email,
							role: g.role,
							company_name: g.company_name,
							created_at: '',
							updated_at: ''
						}));
					}
				} else {
					apiGardeners = await getGardeners();
				}
				setGardeners(apiGardeners);
			} catch (err) {
				console.error('Failed to load gardeners:', err);
			}
		} catch (err) {
			console.error('Failed to load data:', err);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		loadData();
	}, [user, workspaceListRev, user?.activeCompanyId, viewAsTarget]);

	useEffect(() => {
		let cancelled = false;
		const lensCoG = user ? getAdminEffectiveCompanyOrGardenerIdNum(user, viewAsTarget) : null;
		const effectiveCompanyUserId =
			user?.role === 'admin' && lensCoG != null ? String(lensCoG) : user?.id ? String(user.id) : '';
		if (user?.role !== 'company' && user?.role !== 'gardener' && !(user?.role === 'admin' && lensCoG != null)) {
			setCompanyCountryCode(null);
			return;
		}
		if (!effectiveCompanyUserId) return;
		getMyCompany(effectiveCompanyUserId, user?.activeCompanyId ?? undefined)
			.then((company) => {
				if (!cancelled) setCompanyCountryCode(company?.company?.country_code || null);
			})
			.catch(() => {
				if (!cancelled) setCompanyCountryCode(null);
			});
		return () => {
			cancelled = true;
		};
	}, [user?.role, user?.activeCompanyId, user?.id, viewAsTarget]);

	// Keep scope defaults aligned with Calendar as the user changes (e.g. after login)
	useEffect(() => {
		setScopeFilter(prev => {
			if (user?.role === 'company' || user?.role === 'admin') return prev === 'self' || prev === 'my_projects' ? prev : 'company';
			if (user?.role === 'gardener') return prev === 'company' ? 'self' : prev;
			return 'company';
		});
	}, [user?.role]);

	// Update selectedTask when databaseTasks or projects change (after refresh)
	useEffect(() => {
		if (selectedTask && selectedTask.task.id.startsWith('db-')) {
			const taskId = parseInt(selectedTask.task.id.replace('db-', ''));
			const updatedDbTask = databaseTasks.find(t => t.id === taskId);
			if (updatedDbTask) {
				const project = projects.find(p => String(p.id) === String(updatedDbTask.project_id));
				if (project) {
					const updatedGanttTask = dbTaskToGanttTask(updatedDbTask, project);
					setSelectedTask({ task: updatedGanttTask, project });
				}
			}
		}
	}, [databaseTasks, projects]);

	// Combine database tasks and Gantt tasks (prioritize database tasks)
	const allTasks = useMemo(() => {
		// Use a Map keyed by unique string to ensure absolute uniqueness
		// Key format: "db-{taskId}" for database tasks
		const uniqueTaskMap = new Map<string, { task: GanttTask; project: Project }>();
		
		// ONLY use database tasks - ignore project.ganttData to prevent duplicates
		databaseTasks.forEach(dbTask => {
			const uniqueKey = `db-${dbTask.id}`;
			
			// Skip if we've already added this task
			if (uniqueTaskMap.has(uniqueKey)) {
				console.warn(`[Tasks] Duplicate task detected: ${dbTask.title} (ID: ${dbTask.id}) - skipping`);
				return;
			}
			
			if (dbTask.project_id) {
				const project = projects.find(p => String(p.id) === String(dbTask.project_id));
				if (project) {
					const ganttTask = dbTaskToGanttTask(dbTask, project);
					uniqueTaskMap.set(uniqueKey, { task: ganttTask, project });
				}
			}
		});
		
		// Convert map to array - this guarantees no duplicates
		const tasks = Array.from(uniqueTaskMap.values());
		
		return tasks;
	}, [databaseTasks, projects]);

	// Support deep links from notifications:
	// /app/tasks?project_id=X&task_id=Y&issue_id=Z
	useEffect(() => {
		const projectIdParam = searchParams.get('project_id');
		const taskIdParam = searchParams.get('task_id');
		if (!projectIdParam && !taskIdParam) return;

		if (projectIdParam && projectFilterId !== projectIdParam) {
			setProjectFilterId(projectIdParam);
		}

		if (taskIdParam) {
			const taskIdNum = parseInt(taskIdParam, 10);
			if (!Number.isNaN(taskIdNum)) {
				const matched = allTasks.find(({ task }) => task.id === `db-${taskIdNum}`);
				if (matched) {
					// Ensure the task remains visible in list context
					setStatusFilter('all');
					setTimeFilter('all');
					setProjectFilterId(String(matched.project.id));

					if (!selectedTask || selectedTask.task.id !== matched.task.id) {
						setSelectedTask(matched);
					}

					// Remove one-shot params so modal doesn't reopen repeatedly on close
					const nextParams = new URLSearchParams(searchParams);
					nextParams.delete('task_id');
					nextParams.delete('issue_id');
					setSearchParams(nextParams, { replace: true });
				}
			}
		}
	}, [allTasks, projectFilterId, searchParams, selectedTask, setSearchParams]);

	const effectiveUserId = useMemo(() => {
		if (!user?.id) return null;
		const n = parseInt(String(user.id), 10);
		return Number.isFinite(n) && !isNaN(n) ? String(n) : String(user.id);
	}, [user?.id]);

	const projectIdsWhereUserAssignedAnyTask = useMemo(() => {
		if (!effectiveUserId) return new Set<string>();
		const ids = new Set<string>();
		for (const { task, project } of allTasks) {
			const taskGardenerIds = task.gardenerIds || ((task as any).gardenerId ? [(task as any).gardenerId] : []);
			if (taskGardenerIds.map(String).includes(String(effectiveUserId))) {
				ids.add(String(project.id));
			}
		}
		return ids;
	}, [allTasks, effectiveUserId]);

	const getTaskPrimaryDate = (task: GanttTask): Date | null => {
		const rawEnd = (task as any).endDate;
		const rawStart = (task as any).startDate;
		const end = rawEnd ? new Date(rawEnd) : null;
		if (end && !isNaN(end.getTime())) return end;
		const start = rawStart ? new Date(rawStart) : null;
		if (start && !isNaN(start.getTime())) return start;
		return null;
	};

	const startOfToday = useMemo(() => {
		const d = new Date();
		d.setHours(0, 0, 0, 0);
		return d;
	}, []);

	const startOfWeek = (d: Date) => {
		const copy = new Date(d);
		const day = copy.getDay(); // 0=Sun
		const diff = copy.getDate() - day + (day === 0 ? -6 : 1); // Mon start
		copy.setDate(diff);
		copy.setHours(0, 0, 0, 0);
		return copy;
	};

	const endOfWeek = (d: Date) => {
		const start = startOfWeek(d);
		const end = new Date(start);
		end.setDate(end.getDate() + 7);
		return end;
	};

	const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
	const endOfMonthExclusive = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 1);

	// Filter tasks by project + scope + status + time/date range
	const filteredTasks = useMemo(() => {
		const now = new Date();
		const endTodayExclusive = new Date(startOfToday);
		endTodayExclusive.setDate(endTodayExclusive.getDate() + 1);

		// Range filter boundaries (inclusive on both ends for date-only inputs)
		const rangeFromDate = rangeFrom ? new Date(`${rangeFrom}T00:00:00`) : null;
		const rangeToExclusive = rangeTo ? new Date(`${rangeTo}T00:00:00`) : null;
		if (rangeToExclusive) rangeToExclusive.setDate(rangeToExclusive.getDate() + 1);

		const isCompleted = (t: GanttTask) => t.status === 'completed';

		const dateMatches = (d: Date | null) => {
			// No valid date: keep it visible in broad views (Upcoming/All),
			// but hide it in date-constrained buckets (Overdue/Past/This week/month/Next X/Custom range).
			if (!d) return timeFilter === 'upcoming' || timeFilter === 'all';

			if (timeFilter === 'range') {
				if (rangeFromDate && d < rangeFromDate) return false;
				if (rangeToExclusive && d >= rangeToExclusive) return false;
				return true;
			}

			switch (timeFilter) {
				case 'upcoming':
					return d >= startOfToday;
				case 'overdue':
					return d < startOfToday;
				case 'past':
					return d < startOfToday;
				case 'this_week': {
					const from = startOfWeek(now);
					const to = endOfWeek(now);
					return d >= from && d < to;
				}
				case 'this_month': {
					const from = startOfMonth(now);
					const to = endOfMonthExclusive(now);
					return d >= from && d < to;
				}
				case 'next_30': {
					const to = new Date(endTodayExclusive);
					to.setDate(to.getDate() + 30);
					return d >= startOfToday && d < to;
				}
				case 'next_90': {
					const to = new Date(endTodayExclusive);
					to.setDate(to.getDate() + 90);
					return d >= startOfToday && d < to;
				}
				case 'all':
				default:
					return true;
			}
		};

		return allTasks.filter(({ task, project }) => {
			// Project filter
			if (projectFilterId && String(project.id) !== projectFilterId) return false;

			// Scope filter (Only me / My projects / All company)
			if (scopeFilter !== 'company') {
				if (!effectiveUserId) return false;

				const taskGardenerIds = task.gardenerIds || ((task as any).gardenerId ? [(task as any).gardenerId] : []);
				const isAssignedToMe = taskGardenerIds.map(String).includes(String(effectiveUserId));

				if (scopeFilter === 'self' && !isAssignedToMe) return false;

				if (scopeFilter === 'my_projects') {
					const projectHasMe = Array.isArray(project.gardenerIds) && project.gardenerIds.map(String).includes(String(effectiveUserId));
					const projectHasAnyAssignedToMe = projectIdsWhereUserAssignedAnyTask.has(String(project.id));
					if (!projectHasMe && !projectHasAnyAssignedToMe) return false;
				}
			}

			// Status filter
			if (statusFilter === 'open' && isCompleted(task)) return false;
			if (statusFilter === 'completed' && !isCompleted(task)) return false;

			// Time filter: by default, keep overdue hidden unless explicitly selected.
			const primaryDate = getTaskPrimaryDate(task);
			if (timeFilter === 'overdue') {
				// Overdue should only show tasks that aren't completed
				if (isCompleted(task)) return false;
				return primaryDate ? primaryDate < startOfToday : false;
			}
			if (timeFilter === 'upcoming') {
				// Upcoming hides past items; completed tasks can be seen via Status=Completed or Time=All/Past.
				return dateMatches(primaryDate);
			}
			if (timeFilter === 'past') {
				return dateMatches(primaryDate);
			}

			return dateMatches(primaryDate);
		});
	}, [
		allTasks,
		projectFilterId,
		scopeFilter,
		effectiveUserId,
		projectIdsWhereUserAssignedAnyTask,
		statusFilter,
		timeFilter,
		rangeFrom,
		rangeTo,
		startOfToday
	]);

	// Sort by start date and ensure no duplicates by task ID
	const sortedTasks = useMemo(() => {
		// Final deduplication pass using a Set of task IDs
		const seenTaskIds = new Set<string>();
		const uniqueTasks: Array<{ task: GanttTask; project: Project }> = [];
		
		filteredTasks.forEach(({ task, project }) => {
			const taskKey = task.id; // Use the task.id as unique key
			if (!seenTaskIds.has(taskKey)) {
				seenTaskIds.add(taskKey);
				uniqueTasks.push({ task, project });
			} else {
				console.warn(`[Tasks] Duplicate task in sorted list: ${task.name} (ID: ${task.id}) - skipping`);
			}
		});
		
		// Sort the unique tasks
		return uniqueTasks.sort((a, b) => {
			const dateA = new Date(a.task.startDate).getTime();
			const dateB = new Date(b.task.startDate).getTime();
			return dateA - dateB;
		});
	}, [filteredTasks]);

	const getProgressMeta = (progress?: number): { color: 'secondary' | 'warning' | 'success'; value: number } => {
		const normalized = Math.max(0, Math.min(100, progress ?? 0));
		if (normalized >= 100) return { color: 'success', value: 100 };
		if (normalized > 0) return { color: 'warning', value: normalized };
		return { color: 'secondary', value: 0 };
	};

	const getGardenerName = (gardenerId?: string): string => {
		if (!gardenerId) return 'Unassigned';
		const gardener = gardeners.find(g => String(g.id) === gardenerId);
		return gardener?.name || 'Unknown';
	};

	const projectRowShades = [
		'rgba(25, 135, 84, 0.10)',
		'rgba(25, 135, 84, 0.15)',
		'rgba(25, 135, 84, 0.20)',
		'rgba(25, 135, 84, 0.25)',
		'rgba(25, 135, 84, 0.30)'
	];

	const getProjectRowBackground = (projectId: string | number): string => {
		const normalized = String(projectId ?? '');
		let hash = 0;
		for (let i = 0; i < normalized.length; i++) {
			hash = (hash << 5) - hash + normalized.charCodeAt(i);
			hash |= 0;
		}
		const idx = Math.abs(hash) % projectRowShades.length;
		return projectRowShades[idx];
	};

	const getProjectRowCellStyle = (projectId: string | number) => ({
		backgroundColor: getProjectRowBackground(projectId)
	});

	if (isLoading) {
		return (
			<div className="container-fluid py-4">
				<CompanyWorkspaceStrip />
				<div className="text-center py-5">
					<div className="spinner-border text-primary" role="status">
						<span className="visually-hidden">Loading tasks...</span>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="container-fluid py-4">
			<CompanyWorkspaceStrip />
			<div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-2">
				<h1 className="h3 mb-0">{copy('Tasks', 'Aufgaben', 'Zadaci')}</h1>
				<div className="d-flex align-items-center gap-2">
					{(user?.role === 'gardener' || user?.role === 'company' || user?.role === 'admin') && (
						<div className="btn-group" role="group" aria-label="Tasks scope">
							<button
								type="button"
								className={`btn btn-sm ${scopeFilter === 'self' ? 'btn-success' : 'btn-outline-secondary'}`}
								onClick={() => setScopeFilter('self')}
							>
								{copy('Only me', 'Nur ich', 'Samo ja')}
							</button>
							<button
								type="button"
								className={`btn btn-sm ${scopeFilter === 'my_projects' ? 'btn-success' : 'btn-outline-secondary'}`}
								onClick={() => setScopeFilter('my_projects')}
							>
								{copy('My projects', 'Meine Projekte', 'Moji projekti')}
							</button>
							{(user?.role === 'company' || user?.role === 'admin') && (
								<button
									type="button"
									className={`btn btn-sm ${scopeFilter === 'company' ? 'btn-success' : 'btn-outline-secondary'}`}
									onClick={() => setScopeFilter('company')}
								>
									{copy('All company', 'Ganzes Unternehmen', 'Cijela tvrtka')}
								</button>
							)}
						</div>
					)}
					<select
						className="form-select form-select-sm"
						style={{ width: 'auto', minWidth: 140 }}
						value={statusFilter}
						onChange={(e) => setStatusFilter(e.target.value as any)}
						title="Status"
					>
						<option value="open">{copy('Open', 'Offen', 'Otvoreno')}</option>
						<option value="completed">{copy('Completed', 'Abgeschlossen', 'Završeno')}</option>
						<option value="all">{copy('All statuses', 'Alle Status', 'Svi statusi')}</option>
					</select>
					<select
						className="form-select form-select-sm"
						style={{ width: 'auto', minWidth: 170 }}
						value={timeFilter}
						onChange={(e) => setTimeFilter(e.target.value as any)}
						title="Time"
					>
						<option value="upcoming">{copy('Upcoming', 'Bevorstehend', 'Nadolazeće')}</option>
						<option value="overdue">{copy('Overdue', 'Überfällig', 'Zakašnjelo')}</option>
						<option value="this_week">{copy('This week', 'Diese Woche', 'Ovaj tjedan')}</option>
						<option value="this_month">{copy('This month', 'Diesen Monat', 'Ovaj mjesec')}</option>
						<option value="next_30">{copy('Next 30 days', 'Nächste 30 Tage', 'Sljedećih 30 dana')}</option>
						<option value="next_90">{copy('Next 90 days', 'Nächste 90 Tage', 'Sljedećih 90 dana')}</option>
						<option value="past">{copy('Past', 'Vergangenheit', 'Prošlo')}</option>
						<option value="all">{copy('All time', 'Gesamte Zeit', 'Sve vrijeme')}</option>
						<option value="range">{copy('Custom range', 'Benutzerdefinierter Bereich', 'Prilagođeni raspon')}</option>
					</select>
					{timeFilter === 'range' && (
						<div className="d-flex align-items-center gap-2">
							<input
								type="date"
								className="form-control form-control-sm"
								value={rangeFrom}
								onChange={(e) => setRangeFrom(e.target.value)}
								title="From"
							/>
							<input
								type="date"
								className="form-control form-control-sm"
								value={rangeTo}
								onChange={(e) => setRangeTo(e.target.value)}
								title="To"
							/>
						</div>
					)}
					<select
						className="form-select form-select-sm"
						style={{ width: 'auto', minWidth: 200 }}
						value={projectFilterId}
						onChange={(e) => setProjectFilterId(e.target.value)}
					>
						<option value="">{copy('All projects', 'Alle Projekte', 'Svi projekti')}</option>
						{projects.map((p) => (
							<option key={p.id} value={String(p.id)}>
								{getProjectDisplayName(p)}
								{p.companyName ? ` · ${p.companyName}` : ''}
							</option>
						))}
					</select>
					<button
						className="btn btn-success"
						onClick={() => setShowCreateModal(true)}
					>
						<i className="bi bi-plus-circle me-2"></i>{copy('Add Task', 'Aufgabe hinzufügen', 'Dodaj zadatak')}
					</button>
				</div>
			</div>
			{(user?.role === 'company' || user?.role === 'gardener') && (
				<p className="text-muted small mb-3">
					{copy('Viewing:', 'Ansicht:', 'Prikaz:')}{' '}
					<strong>
						{getStoredCompanyScopeMode() === 'all_companies'
							? copy('All companies', 'Alle Unternehmen', 'Sve tvrtke')
							: user.companyName?.trim() || copy('Active company', 'Aktives Unternehmen', 'Aktivna tvrtka')}
					</strong>
				</p>
			)}
			<div className="card">
				<div className="card-body">
					{sortedTasks.length === 0 ? (
						<div className="alert alert-info">
							<strong>{copy('No tasks found.', 'Keine Aufgaben gefunden.', 'Nema pronađenih zadataka.')}</strong>
							<p className="mb-0 mt-2">
								{allTasks.length === 0
									? copy(
										'No timeline tasks available. Tasks will appear here once projects have timeline data.',
										'Keine Timeline-Aufgaben verfügbar. Aufgaben erscheinen hier, sobald Projekte Zeitplandaten haben.',
										'Nema dostupnih zadataka vremenske crte. Zadaci će se prikazati kada projekti imaju podatke vremenske crte.'
									)
									: copy('No tasks are assigned to you.', 'Ihnen sind keine Aufgaben zugewiesen.', 'Nema dodijeljenih zadataka.')}
							</p>
						</div>
					) : (
						<div className="table-responsive">
							<table className="table align-middle table-hover">
								<thead>
									<tr>
										<th style={{ width: '39%' }}>{copy('Task', 'Aufgabe', 'Zadatak')}</th>
										<th>{copy('Project', 'Projekt', 'Projekt')}</th>
										{getStoredCompanyScopeMode() === 'all_companies' &&
										(user?.role === 'company' || user?.role === 'gardener') ? (
											<th>{copy('Company', 'Unternehmen', 'Tvrtka')}</th>
										) : null}
										<th>{copy('Start Date', 'Startdatum', 'Datum početka')}</th>
										<th>{copy('End Date', 'Enddatum', 'Datum završetka')}</th>
										<th>{copy('Days', 'Tage', 'Dani')}</th>
										<th className="text-nowrap" style={{ minWidth: '110px' }}>{copy('Assigned To', 'Zugewiesen an', 'Dodijeljeno')}</th>
										<th>{copy('Progress', 'Fortschritt', 'Napredak')}</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									{sortedTasks.map(({ task, project }, index) => {
										// Use gardener names from API response if available, otherwise fallback to lookup
										const gardenerNames = (task as any).gardenerNames || [];
										const assignedGardeners = gardenerNames.length > 0
											? gardenerNames.filter((name: string) => name && name.trim() !== '')
											: (() => {
												// Fallback: look up names from gardenerIds
												const taskGardenerIds = task.gardenerIds || (task.gardenerId ? [task.gardenerId] : []);
												const uniqueGardenerIds = Array.from(new Set(
													taskGardenerIds
														.filter(id => id && String(id).trim() !== '')
														.map(id => String(id).trim())
												));
												return uniqueGardenerIds
													.map(id => getGardenerName(id))
													.filter(name => name && name !== 'Unassigned' && name !== 'Unknown');
											})();
										const progressMeta = getProgressMeta(task.progress);
										const legalMeta = getTaskLegalMeta(task, companyCountryCode);
										
										return (
											<tr
												key={`task-${task.id}-proj-${project.id}-${index}`}
												style={{ cursor: 'pointer' }}
												onClick={() => setSelectedTask({ task, project })}
											>
												<td className="fw-medium" style={getProjectRowCellStyle(project.id)}>
													<div className="d-flex align-items-center gap-2 flex-wrap">
														<span>{task.name}</span>
														<TaskLegalBadge legalMeta={legalMeta} copy={copy} />
														{((task as any).open_issue_count ?? 0) > 0 ? (
															<span
																className="badge text-bg-danger"
																title={copy(
																	`${(task as any).open_issue_count} open issue(s)`,
																	`${(task as any).open_issue_count} offene(s) Problem(e)`,
																	`${(task as any).open_issue_count} otvorenih problema`
																)}
															>
																<i className="bi bi-exclamation-triangle-fill me-1" aria-hidden />
																{(task as any).open_issue_count}
															</span>
														) : null}
													</div>
													{task.description ? (
														<div className="small text-muted mt-1 d-none d-md-block">{task.description}</div>
													) : null}
												</td>
												<td
													style={getProjectRowCellStyle(project.id)}
													onClick={(e) => { e.stopPropagation(); navigate(`/app/gardener/project/${project.id}`); }}
												>
													<span
														className="text-success text-decoration-underline"
														style={{ cursor: 'pointer' }}
														title={project.seekerEmail || undefined}
													>
														{getProjectDisplayName(project)}
													</span>
												</td>
												{getStoredCompanyScopeMode() === 'all_companies' &&
												(user?.role === 'company' || user?.role === 'gardener') ? (
													<td style={getProjectRowCellStyle(project.id)}>
														{project.companyName || task.projectCompanyName ? (
															<span className="badge text-bg-secondary text-wrap text-start">
																{project.companyName || task.projectCompanyName}
															</span>
														) : (
														<span className="text-muted small">—</span>
														)}
													</td>
												) : null}
												<td style={getProjectRowCellStyle(project.id)}>{new Date(task.startDate).toLocaleDateString('en-GB')}</td>
												<td style={getProjectRowCellStyle(project.id)}>{new Date(task.endDate).toLocaleDateString('en-GB')}</td>
												<td style={getProjectRowCellStyle(project.id)}>{task.duration || 0}</td>
												<td className="text-nowrap" style={getProjectRowCellStyle(project.id)}>
													{assignedGardeners.length > 0 ? (
														<div className="small">
															{assignedGardeners.join(', ')}
														</div>
													) : (
														<span className="text-muted small">{copy('Unassigned', 'Nicht zugewiesen', 'Nedodijeljeno')}</span>
													)}
												</td>
												<td style={getProjectRowCellStyle(project.id)}>
													<div className="d-flex align-items-center gap-2">
														<div className="progress" style={{ width: '120px', height: '20px' }}>
															<div
																className={`progress-bar bg-${progressMeta.color}`}
																role="progressbar"
																style={{ width: `${progressMeta.value}%` }}
																aria-valuenow={progressMeta.value}
																aria-valuemin={0}
																aria-valuemax={100}
															>
																{progressMeta.value}%
															</div>
														</div>
													</div>
												</td>
												<td className="text-end" style={getProjectRowCellStyle(project.id)} onClick={(e) => e.stopPropagation()}>
													<button 
														className="btn btn-sm btn-outline-success me-2"
														onClick={() => setSelectedTask({ task, project })}
													>
														{copy('View', 'Öffnen', 'Pregled')}
													</button>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>

			{/* Task Detail Modal */}
			{selectedTask && (
				<TaskDetailModal
					task={selectedTask.task}
					project={selectedTask.project}
					companyCountryCode={companyCountryCode}
					gardeners={gardeners}
					onClose={() => setSelectedTask(null)}
					onTaskPatched={(taskId, patch) => {
						setDatabaseTasks((prev) =>
							prev.map((t) => (String(t.id) === String(taskId) ? { ...t, ...patch } : t))
						);
						if (selectedTask?.task.id === `db-${taskId}`) {
							setSelectedTask((prev) => {
								if (!prev || prev.task.id !== `db-${taskId}`) return prev;
								const mergedTask = {
									...prev.task,
									name: patch.title ?? prev.task.name,
									description: patch.description ?? prev.task.description,
									status: (patch.status as GanttTask['status']) ?? prev.task.status,
									progress: patch.progress ?? prev.task.progress,
									startDate: patch.start_date
										? new Date(patch.start_date).toISOString()
										: prev.task.startDate,
									endDate: patch.end_date
										? new Date(patch.end_date).toISOString()
										: prev.task.endDate,
									duration: patch.duration ?? prev.task.duration,
									notes: patch.notes ?? prev.task.notes,
									gardenerIds: patch.gardener_ids
										? patch.gardener_ids.map((id) => String(id))
										: prev.task.gardenerIds
								};
								return { ...prev, task: mergedTask };
							});
						}
					}}
					onUpdate={async () => {
						await loadData();
					}}
				/>
			)}

			{/* Task Create Modal */}
			{showCreateModal && (
				<TaskCreateModal
					gardeners={gardeners}
					multiCompanyCreate={getStoredCompanyScopeMode() === 'all_companies'}
					requesterUserId={
						user?.id ? parseInt(String(user.id), 10) : undefined
					}
					onClose={() => setShowCreateModal(false)}
					onSuccess={() => {
						loadData();
						setShowCreateModal(false);
					}}
				/>
			)}
		</div>
	);
}


